import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getJobResults } from '../services/photoMetadataService';

const determineIsProcessing = (job) => {
  if (!job) {
    return false;
  }

  if (job.status === 'completed' || job.status === 'failed') {
    return false;
  }

  if (job.status === 'pending' || job.status === 'processing') {
    if (job.results && job.results.length > 0) {
      const pendingPhotos = job.results.filter((result) => result.status === 'pending');
      if (pendingPhotos.length === 0) {
        return false;
      }
    }

    return true;
  }

  return false;
};

const useJobResultsData = (
  jobId,
  {
    onAutoSelect,
    isClientBatchApplying = false,
    onServerBatchApplyDetected
  } = {}
) => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const shouldContinuePolling = useCallback(
    (jobData) => {
      if (jobData.applyProgress?.isApplying) {
        if (onServerBatchApplyDetected) {
          onServerBatchApplyDetected();
        }
        return true;
      }

      if (isClientBatchApplying) {
        return true;
      }

      if (jobData.status === 'completed' || jobData.status === 'failed') {
        if (jobData.applyProgress && !jobData.applyProgress.isApplying) {
          setTimeout(() => stopPolling(), 2000);
        }
        return false;
      }

      if (jobData.status === 'pending' || jobData.status === 'processing') {
        if (jobData.results && jobData.results.length > 0) {
          const pendingPhotos = jobData.results.filter((result) => result.status === 'pending');
          if (pendingPhotos.length === 0) {
            return false;
          }
        }
        return true;
      }

      return false;
    },
    [isClientBatchApplying, onServerBatchApplyDetected, stopPolling]
  );

  const fetchJob = useCallback(
    async ({ showToastOnError = true, autoSelect = false } = {}) => {
      try {
        const jobData = await getJobResults(jobId);
        setJob(jobData);

        if (jobData.status === 'payment_pending') {
          navigate(`/payment/${jobId}`);
          return { continuePolling: false };
        }

        if (autoSelect && typeof onAutoSelect === 'function') {
          onAutoSelect(jobData);
        }

        return { continuePolling: shouldContinuePolling(jobData) };
      } catch (error) {
        if (showToastOnError) {
          toast.error('Failed to load job details');
        }
        return { continuePolling: false };
      } finally {
        setLoading(false);
      }
    },
    [jobId, navigate, onAutoSelect, shouldContinuePolling]
  );

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      return;
    }

    setIsPolling(true);
    pollingIntervalRef.current = setInterval(async () => {
      const { continuePolling } = await fetchJob({ showToastOnError: false });

      if (!continuePolling) {
        stopPolling();
      }
    }, 3000);
  }, [fetchJob, stopPolling]);

  useEffect(() => {
    setJob(null);
    setLoading(true);

    const initializePage = async () => {
      const { continuePolling } = await fetchJob({ showToastOnError: true, autoSelect: true });

      if (continuePolling) {
        startPolling();
      }
    };

    initializePage();

    return () => {
      stopPolling();
    };
  }, [fetchJob, jobId, startPolling, stopPolling]);

  const provider = job?.provider || 'flickr';
  const providerName = useMemo(
    () => provider.charAt(0).toUpperCase() + provider.slice(1),
    [provider]
  );

  const isProcessing = useMemo(() => determineIsProcessing(job), [job]);

  return {
    job,
    setJob,
    loading,
    isPolling,
    startPolling,
    stopPolling,
    fetchJob,
    provider,
    providerName,
    isProcessing
  };
};

export default useJobResultsData;
